"use client";

import { RiMobileDownloadLine } from "@remixicon/react";
import type { Review } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import { useCallback, useState } from "react";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { useCalendarPreferences } from "@/hooks/shared/use-calendar-preferences";
import { useSignupSuggestionStore } from "@/lib/stores/signup-suggestion-store";
import { ExportSetModal } from "./export-set-modal";
import { SignupSuggestionModal } from "./signup-suggestion-modal";

interface ExportScheduleButtonProps {
	festivalName: string;
	festivalSlug: string;
	festivalId: string;
	selectedDay: string | null;
	availableDays: string[];
	username?: string;
	userProfileImageUrl?: string;
	disabled?: boolean;
	sortAscending?: boolean;
	calendarData?: Array<{
		id: string;
		artist: string;
		stage: string;
		time: string;
		startHour: number;
		duration: number;
		day: string;
		imageUrl?: string | null;
		status: {
			isInSchedule: boolean;
			sawLive: boolean;
			sawStream: boolean;
			mustSee: boolean;
		};
	}>;
	reviews?: Review[];
	stages?: Array<{
		id: string;
		name: string;
		position: number;
	}>;
	eventType?: "main_festival" | "pre_party" | "after_party";
}

export function Exportsetbutton({
	festivalName,
	festivalSlug,
	festivalId,
	selectedDay,
	availableDays,
	username,
	userProfileImageUrl,
	disabled = false,
	sortAscending = false,
	calendarData = [],
	reviews = [],
	stages = [],
	eventType,
}: ExportScheduleButtonProps) {
	const [showOptions, setShowOptions] = useState(false);
	const [showSignupSuggestion, setShowSignupSuggestion] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const { user } = useSupabase();
	const { preferences: calendarPreferences } = useCalendarPreferences();
	const { shouldShowSuggestion, dismissSuggestion } =
		useSignupSuggestionStore();

	const handleExportClick = () => {
		if (!selectedDay || disabled) return;
		if (!user && shouldShowSuggestion(festivalSlug)) {
			setShowSignupSuggestion(true);
		} else {
			setShowOptions(true);
		}
	};

	const handleContinueWithoutSignIn = useCallback(() => {
		dismissSuggestion(festivalSlug);
		setShowSignupSuggestion(false);
		setShowOptions(true);
	}, [dismissSuggestion, festivalSlug]);

	return (
		<>
			<Button
				variant="secondary"
				size="sm"
				onClick={handleExportClick}
				disabled={!selectedDay || disabled || isExporting}
				className="w-8 p-0"
				aria-label="Export schedule"
				title="Export schedule"
			>
				{isExporting ? (
					<Spinner className="h-4 w-4 animate-spin" />
				) : (
					<RiMobileDownloadLine className="h-4 w-4" />
				)}
			</Button>

			<SignupSuggestionModal
				open={showSignupSuggestion}
				onOpenChange={setShowSignupSuggestion}
				onContinueWithoutSignIn={handleContinueWithoutSignIn}
				festivalSlug={festivalSlug}
				festivalName={festivalName}
			/>

			{showOptions && selectedDay && (
				<ExportSetModal
					open={showOptions}
					onOpenChange={setShowOptions}
					onExportingChange={setIsExporting}
					festivalName={festivalName}
					festivalSlug={festivalSlug}
					festivalId={festivalId}
					selectedDay={selectedDay}
					availableDays={availableDays}
					username={username}
					userProfileImageUrl={userProfileImageUrl}
					sortAscending={sortAscending}
					calendarData={calendarData}
					reviews={reviews}
					stages={stages}
					eventType={eventType}
					defaultShowArtistPhotos={calendarPreferences?.show_artist_photos ?? true}
				/>
			)}
		</>
	);
}
